/**
 * Thin gtag wrapper. Side-effect only — never renders anything.
 *
 * - Calls are safe pre-consent: gtag.js drops events when `analytics_storage`
 *   is denied (per Consent Mode v2). We don't pre-gate to keep the wrapper
 *   simple and to let GA4 handle consent deterministically.
 * - Calls are safe pre-load: `window.gtag` is defined as a dataLayer push
 *   before gtag.js loads (see layout.tsx Consent Mode v2 default script), so
 *   queued events are flushed once GA4 is ready.
 * - PII is stripped: any property value that looks like an email address or a
 *   phone number is replaced with a coarse domain/country code.
 */

type EventParams = Record<string, unknown>

const EMAIL_RE = /[\w.+-]+@([\w-]+\.[\w.-]+)/
const PHONE_RE = /(?:\+?\d[\s\d().-]{6,}\d)/

function sanitizeValue(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const emailMatch = value.match(EMAIL_RE)
  if (emailMatch) return `email@${emailMatch[1]}`
  if (PHONE_RE.test(value)) return 'phone_redacted'
  return value
}

function sanitize(params: EventParams): EventParams {
  const out: EventParams = {}
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null) continue
    out[key] = sanitizeValue(raw)
  }
  return out
}

const CTX_KEY = '__lmAnalyticsCtx'

function getCtx(): EventParams {
  if (typeof window === 'undefined') return {}
  return (window as unknown as Record<string, EventParams>)[CTX_KEY] ?? {}
}

export function track(name: string, params: EventParams = {}): void {
  if (typeof window === 'undefined') return
  const gtag = window.gtag
  if (typeof gtag !== 'function') return
  gtag('event', name, {
    page_path: window.location?.pathname,
    ...getCtx(),
    ...sanitize(params),
  })
}

/**
 * Stores per-page dimensions (page_type, cms_collection, …) on `window` so
 * every subsequent `track()` call merges them in as event params.
 *
 * Uses a window-scoped key instead of a module-level `let` because Next.js
 * code-splitting can duplicate this module across chunks — each duplicate
 * would otherwise have its own empty cache, so dimensions set in one chunk
 * (AnalyticsPageContext) would never be read by another (AnalyticsAutoTracker).
 *
 * Why not `gtag('set', …)`? In GA4 the `set` command does not propagate
 * event-scoped custom dimensions to downstream `event` calls — they arrive
 * as `(not set)` in reports. Attaching them explicitly per event is the
 * only reliable way.
 */
export function setPageContext(params: EventParams): void {
  if (typeof window === 'undefined') return
  ;(window as unknown as Record<string, EventParams>)[CTX_KEY] = sanitize(params)
}

/**
 * Best-effort lead classification from a form title or id. Lets us segment
 * conversions by intent (offerte vs. bewerbung vs. kontakt) in GA4 without
 * relying on per-form configuration.
 */
export function deriveLeadType(rawName: string | null | undefined): string {
  if (!rawName) return 'unknown'
  const name = rawName.toLowerCase()
  if (name.includes('offerte')) return 'offerte'
  if (name.includes('bewerb')) return 'bewerbung'
  if (name.includes('ausbild') || name.includes('lehr')) return 'ausbildung'
  if (name.includes('newsletter')) return 'newsletter'
  if (name.includes('kontakt')) return 'kontakt'
  return 'other'
}
